/**
 * Public Network Status Page
 * View system status and active issues
 */

import React, { useState, useEffect } from 'react';
import {
  Activity,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Wrench,
  Server,
  Calendar,
  Bell,
  Clock,
} from 'lucide-react';

interface NetworkServer {
  id: number;
  name: string;
  status: 'operational' | 'degraded' | 'down' | 'maintenance';
  uptime_percentage: number;
}

interface NetworkIssue {
  id: number;
  title: string;
  description: string;
  type: 'incident' | 'maintenance';
  severity: 'minor' | 'major' | 'critical';
  status: string;
  affected_services: string | null;
  started_at: string;
  resolved_at: string | null;
  scheduled_start: string | null;
  scheduled_end: string | null;
  updates: string | null;
}

interface IssueUpdate {
  timestamp: string;
  status: string;
  message: string;
}

export const NetworkStatus: React.FC = () => {
  const [overallStatus, setOverallStatus] = useState<string>('operational');
  const [servers, setServers] = useState<NetworkServer[]>([]);
  const [activeIssues, setActiveIssues] = useState<NetworkIssue[]>([]);
  const [recentIssues, setRecentIssues] = useState<NetworkIssue[]>([]);
  const [selectedIssue, setSelectedIssue] = useState<NetworkIssue | null>(null);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatus();
    loadIssues();
  }, []);

  const loadStatus = async () => {
    try {
      const response = await fetch('/api/v1/public/network/status');
      const data = await response.json();
      if (data.success) {
        setOverallStatus(data.data.status);
        setServers(data.data.servers);
      }
    } catch (error) {
      console.error('Failed to load status:', error);
    }
  };

  const loadIssues = async () => {
    setLoading(true);
    try {
      const [activeResponse, recentResponse] = await Promise.all([
        fetch('/api/v1/public/network/issues/active'),
        fetch('/api/v1/public/network/issues?limit=10'),
      ]);

      const [activeData, recentData] = await Promise.all([
        activeResponse.json(),
        recentResponse.json(),
      ]);

      if (activeData.success) {
        setActiveIssues(activeData.data.issues);
      }
      if (recentData.success) {
        setRecentIssues(recentData.data.issues);
      }
    } catch (error) {
      console.error('Failed to load issues:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const response = await fetch('/api/v1/public/network/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: subscribeEmail }),
      });

      const data = await response.json();
      if (data.success) {
        alert('Subscription created! Please check your email to verify.');
        setSubscribeEmail('');
      }
    } catch (error) {
      console.error('Failed to subscribe:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
        return <CheckCircle className="w-8 h-8 text-green-400" />;
      case 'degraded':
        return <AlertTriangle className="w-8 h-8 text-yellow-400" />;
      case 'down':
        return <XCircle className="w-8 h-8 text-red-400" />;
      case 'maintenance':
        return <Wrench className="w-8 h-8 text-orange-400" />;
      default:
        return <Activity className="w-8 h-8 text-slate-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      operational: 'from-green-500 to-green-600',
      degraded: 'from-yellow-500 to-yellow-600',
      down: 'from-red-500 to-red-600',
      maintenance: 'from-blue-500 to-blue-600',
    };
    return colors[status as keyof typeof colors] || 'from-gray-500 to-gray-600';
  };

  const getSeverityBadge = (severity: string) => {
    const colors = {
      minor: 'bg-yellow-900 text-yellow-300',
      major: 'bg-orange-900 text-orange-300',
      critical: 'bg-red-900 text-red-300',
    };
    return colors[severity as keyof typeof colors] || 'bg-slate-700/50 text-slate-300';
  };

  if (selectedIssue) {
    const updates: IssueUpdate[] = selectedIssue.updates ? JSON.parse(selectedIssue.updates) : [];

    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setSelectedIssue(null)}
            className="text-orange-400 hover:text-orange-300 mb-6"
          >
            ← Back to Status
          </button>

          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <span
                  className={`inline-block px-3 py-1 rounded-full text-sm mb-3 ${
                    selectedIssue.type === 'incident'
                      ? 'bg-red-900 text-red-300'
                      : 'bg-blue-900 text-blue-300'
                  }`}
                >
                  {selectedIssue.type}
                </span>
                <h1 className="text-2xl font-bold text-white">
                  {selectedIssue.title}
                </h1>
              </div>
              <span
                className={`px-3 py-1 rounded text-sm ${getSeverityBadge(
                  selectedIssue.severity
                )}`}
              >
                {selectedIssue.severity}
              </span>
            </div>

            <p className="text-slate-300 mb-6">{selectedIssue.description}</p>

            {selectedIssue.affected_services && (
              <div className="bg-gray-750 rounded-lg p-4 mb-6">
                <div className="text-sm text-slate-400 mb-1">Affected Services:</div>
                <div className="text-white">{selectedIssue.affected_services}</div>
              </div>
            )}

            <div className="flex gap-4 text-sm text-slate-400 mb-6">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Started: {new Date(selectedIssue.started_at).toLocaleString()}
              </div>
              {selectedIssue.resolved_at && (
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Resolved: {new Date(selectedIssue.resolved_at).toLocaleString()}
                </div>
              )}
            </div>

            {updates.length > 0 && (
              <div>
                <h2 className="text-lg font-medium text-white mb-4">Updates</h2>
                <div className="space-y-4">
                  {updates.reverse().map((update, idx) => (
                    <div key={idx} className="bg-gray-750 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="px-2 py-1 bg-slate-700/50 text-slate-300 rounded text-sm">
                          {update.status}
                        </span>
                        <span className="text-sm text-slate-400">
                          {new Date(update.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-slate-300">{update.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className={`p-4 bg-gradient-to-br ${getStatusColor(overallStatus)} rounded-full`}>
              {getStatusIcon(overallStatus)}
            </div>
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">System Status</h1>
          <p className="text-xl text-slate-400">
            {overallStatus === 'operational' && 'All Systems Operational'}
            {overallStatus === 'degraded' && 'Some Systems Experiencing Issues'}
            {overallStatus === 'down' && 'Service Disruption'}
            {overallStatus === 'maintenance' && 'Scheduled Maintenance'}
          </p>
        </div>

        {/* Active Issues */}
        {activeIssues.length > 0 && (
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              Active Issues
            </h2>
            <div className="space-y-3">
              {activeIssues.map((issue) => (
                <button
                  key={issue.id}
                  onClick={() => setSelectedIssue(issue)}
                  className="w-full bg-gray-750 hover:bg-slate-700/50 rounded-lg p-4 text-left transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            issue.type === 'incident'
                              ? 'bg-red-900 text-red-300'
                              : 'bg-blue-900 text-blue-300'
                          }`}
                        >
                          {issue.type}
                        </span>
                        <span
                          className={`px-2 py-1 rounded text-xs ${getSeverityBadge(
                            issue.severity
                          )}`}
                        >
                          {issue.severity}
                        </span>
                      </div>
                      <h3 className="text-lg font-medium text-white">{issue.title}</h3>
                      <p className="text-sm text-slate-400 mt-1">
                        {issue.description.substring(0, 100)}...
                      </p>
                    </div>
                    <span className="text-sm text-gray-500 ml-4">
                      {new Date(issue.started_at).toLocaleDateString()}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Server Status */}
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Server className="w-5 h-5" />
            Services
          </h2>
          <div className="space-y-3">
            {servers.map((server) => (
              <div
                key={server.id}
                className="flex items-center justify-between p-4 bg-gray-750 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {getStatusIcon(server.status)}
                  <div>
                    <div className="text-white font-medium">{server.name}</div>
                    <div className="text-sm text-slate-400">
                      Uptime: {server.uptime_percentage.toFixed(2)}%
                    </div>
                  </div>
                </div>
                <span
                  className={`px-3 py-1 rounded text-sm font-medium ${
                    server.status === 'operational'
                      ? 'bg-green-900 text-green-300'
                      : server.status === 'degraded'
                      ? 'bg-yellow-900 text-yellow-300'
                      : server.status === 'down'
                      ? 'bg-red-900 text-red-300'
                      : 'bg-blue-900 text-blue-300'
                  }`}
                >
                  {server.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Issues */}
        {recentIssues.length > 0 && (
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Recent Issues
            </h2>
            <div className="space-y-2">
              {recentIssues.slice(0, 5).map((issue) => (
                <button
                  key={issue.id}
                  onClick={() => setSelectedIssue(issue)}
                  className="w-full flex items-center justify-between p-3 bg-gray-750 hover:bg-slate-700/50 rounded-lg text-left transition-colors"
                >
                  <div className="flex-1">
                    <span className="text-white font-medium">{issue.title}</span>
                    {issue.resolved_at && (
                      <span className="ml-2 text-xs text-green-400">Resolved</span>
                    )}
                  </div>
                  <span className="text-sm text-slate-400">
                    {new Date(issue.started_at).toLocaleDateString()}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Subscribe */}
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Subscribe to Updates
          </h2>
          <p className="text-slate-400 mb-4">
            Get notified about system status changes and maintenance windows.
          </p>
          <form onSubmit={handleSubscribe} className="flex gap-2">
            <input
              type="email"
              value={subscribeEmail}
              onChange={(e) => setSubscribeEmail(e.target.value)}
              placeholder="Enter your email"
              required
              className="flex-1 px-4 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white"
            />
            <button
              type="submit"
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
            >
              Subscribe
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NetworkStatus;
